// src/units_forms/inboundrequests/BulkInboundItemForm.tsx

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from "@mui/material";
import BoxDX from "../../layout/boxdx";
import GridDX from "../../layout/griddx";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { read, utils } from "xlsx";
import ButtonDX from "../../controls/buttondx";
import { useNotificationContext } from "../../../context/notificationcontext";
import { arraysHaveSameElements } from "../../../shared/globals";
import moment from "moment";

interface Props {
  open: boolean;
  handleClose: () => void;
  requestData: any;
  setRequestData: (data: any) => void;
  setIsChanged: (ch: boolean) => void;

  skuDropdownData: any[];
}

const REQUIRED_HEADERS = [
  "skuCode",
  "quantity",
  "uomTypeId",
  "packagingTypeId",
  "expiryDate",
  "batchNumber",
];

const BulkInboundItemForm = (props: Props) => {
  const { t } = useTranslation();
  const { setInfo, setError } = useNotificationContext();
  const {
    open,
    handleClose,
    requestData,
    setRequestData,
    setIsChanged,
    skuDropdownData,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  // Local state to hold parsed items before saving
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedItems, setParsedItems] = useState<any[] | null>(null);

  // trigger hidden input
  const handleClick = () => inputRef.current?.click();

  const onDragDropHandler = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.files.length) return;
    await processFile(e.dataTransfer.files[0]);
  };

  const onChangeHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    await processFile(e.target.files[0]);
  };

  const processFile = async (file: File) => {
    const name = file.name;
    const ext = name.split(".").pop()?.toLowerCase();
    if (!["csv", "xls", "xlsx"].includes(ext || "")) {
      setError(t("Only CSV or Excel files are allowed"));
      return;
    }

    const buffer = await file.arrayBuffer();
    const wb = read(buffer, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const data: any[][] = utils.sheet_to_json(ws, { header: 1 });

    if (!data.length) {
      setError(t("No data found"));
      return;
    }

    const headers = (data[0] as string[]).map((h) => h.trim());
    if (
      !arraysHaveSameElements(
        REQUIRED_HEADERS.map((h) => h.toLowerCase()),
        headers.map((h) => h.toLowerCase()),
      )
    ) {
      setError(
        t("Invalid template. Required columns: {{cols}}", {
          cols: REQUIRED_HEADERS.join(", "),
        }),
      );
      return;
    }

    // build header→index map
    const idxMap = headers.reduce<Record<string, number>>((m, h, i) => {
      m[h.trim().toLowerCase()] = i;
      return m;
    }, {});
    const imported = data
      .slice(1)
      .map((row: any[]) => {
        const rawExpiry = row[idxMap["expirydate"]];
        const expiryDate =
          rawExpiry && moment(rawExpiry).isValid()
            ? moment(rawExpiry).toISOString()
            : null;
        const skuCode = String(row[idxMap["skucode"]]);
        const skuId = skuDropdownData.find(
          (x: any) => x.skuCode == String(row[idxMap["skucode"]]),
        )?.skuId;
        const quantity = Number(row[idxMap["quantity"]]);
        const uomTypeId = Number(row[idxMap["uomtypeid"]]);
        const packagingTypeId = Number(row[idxMap["packagingtypeid"]]);
        const batchNumber = String(row[idxMap["batchnumber"]]) || "";
        console.log("skuCode", skuCode);
        console.log("skuId", skuId);
        console.log("skuDropdownData", skuDropdownData);
        return {
          skuId,
          skuCode,
          quantity,
          uomTypeId,
          packagingTypeId,
          expiryDate,
          batchNumber,
        };
      })
      // filter out any rows whose skuId isn't in your skus list
      .filter((item) => {
        const exists = skuDropdownData.some((s) => s.skuId === item.skuId);
        if (!exists) {
          setError(
            t("SKU with code {{code}} not found", { code: item.skuCode }),
          );
        }
        return exists;
      })
      // now attach IDs and isDeleted flags
      .map((item) => ({
        ...item,
        item_id: Date.now() + Math.random(),
        isDeleted: false,
      }));

    setParsedItems(imported);
    setFileName(name);
  };

  // merge parsedItems into requestData
  const onSave = () => {
    if (!parsedItems || !parsedItems.length) {
      setError(t("No valid rows to import"));
      return;
    }
    setRequestData((prev: any) => ({
      ...prev,
      inboundRequestSkus: [
        ...prev.inboundRequestSkus.filter((i: any) => !i.isDeleted),
        ...parsedItems,
      ],
    }));
    setIsChanged(true);
    setInfo(t("Inbound items successfully added"));
    // cleanup & close
    setParsedItems(null);
    setFileName(null);
    handleClose();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h6">{t("Bulk Import Inbound Items")}</Typography>
      </DialogTitle>
      <DialogContent>
        <GridDX container spacing={2}>
          <GridDX item xs={12}>
            {fileName && parsedItems ? (
              <BoxDX sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Chip
                  label={`${fileName} (${parsedItems.length} rows)`}
                  color="primary"
                  onDelete={() => {
                    setFileName(null);
                    setParsedItems(null);
                  }}
                />
              </BoxDX>
            ) : (
              <BoxDX
                onDragOver={(e: { preventDefault: () => any }) =>
                  e.preventDefault()
                }
                onDrop={onDragDropHandler}
                sx={{
                  border: "1px dashed #c6c6c6",
                  borderRadius: 1,
                  width: "100%",
                  height: 150,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <input
                  type="file"
                  ref={inputRef}
                  hidden
                  accept=".csv,.xls,.xlsx"
                  onChange={onChangeHandler}
                />
                <Typography sx={{ fontSize: 18, textAlign: "center" }}>
                  {t("Drag & Drop file here or")}
                </Typography>
                <ButtonDX variant="text" onClick={handleClick}>
                  {t("Browse")}
                </ButtonDX>
              </BoxDX>
            )}
          </GridDX>
        </GridDX>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <ButtonDX variant="outlined" onClick={handleClose}>
          {t("Close")}
        </ButtonDX>
        <ButtonDX disabled={!parsedItems?.length} onClick={onSave}>
          {t("Save")}
        </ButtonDX>
      </DialogActions>
    </Dialog>
  );
};

export default BulkInboundItemForm;
